import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  MonitorHeart as MonitorIcon,
  RadioButtonChecked as ActiveIcon,
  RadioButtonUnchecked as InactiveIcon,
  Refresh as RefreshIcon,
  SystemUpdateAlt as UpdateIcon,
} from '@mui/icons-material';
import api, { MonitorUnitData } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

// ---------------------------------------------------------------------------
// Region catalogue
// ---------------------------------------------------------------------------
const ALL_REGIONS: Array<{ code: string; label: string; group: string }> = [
  { code: 'us-east-1',      label: 'N. Virginia',      group: 'Americas' },
  { code: 'us-east-2',      label: 'Ohio',              group: 'Americas' },
  { code: 'us-west-1',      label: 'N. California',     group: 'Americas' },
  { code: 'us-west-2',      label: 'Oregon',            group: 'Americas' },
  { code: 'ca-central-1',   label: 'Canada (Montreal)', group: 'Americas' },
  { code: 'ca-west-1',      label: 'Canada (Calgary)',  group: 'Americas' },
  { code: 'sa-east-1',      label: 'São Paulo',         group: 'Americas' },
  { code: 'eu-west-1',      label: 'Ireland',           group: 'Europe' },
  { code: 'eu-west-2',      label: 'London',            group: 'Europe' },
  { code: 'eu-west-3',      label: 'Paris',             group: 'Europe' },
  { code: 'eu-central-1',   label: 'Frankfurt',         group: 'Europe' },
  { code: 'eu-central-2',   label: 'Zurich',            group: 'Europe' },
  { code: 'eu-north-1',     label: 'Stockholm',         group: 'Europe' },
  { code: 'eu-south-1',     label: 'Milan',             group: 'Europe' },
  { code: 'eu-south-2',     label: 'Spain',             group: 'Europe' },
  { code: 'ap-northeast-1', label: 'Tokyo',             group: 'Asia Pacific' },
  { code: 'ap-northeast-2', label: 'Seoul',             group: 'Asia Pacific' },
  { code: 'ap-northeast-3', label: 'Osaka',             group: 'Asia Pacific' },
  { code: 'ap-southeast-1', label: 'Singapore',         group: 'Asia Pacific' },
  { code: 'ap-southeast-2', label: 'Sydney',            group: 'Asia Pacific' },
  { code: 'ap-southeast-3', label: 'Jakarta',           group: 'Asia Pacific' },
  { code: 'ap-southeast-4', label: 'Melbourne',         group: 'Asia Pacific' },
  { code: 'ap-south-1',     label: 'Mumbai',            group: 'Asia Pacific' },
  { code: 'ap-south-2',     label: 'Hyderabad',         group: 'Asia Pacific' },
  { code: 'ap-east-1',      label: 'Hong Kong',         group: 'Asia Pacific' },
  { code: 'me-south-1',     label: 'Bahrain',           group: 'Middle East & Africa' },
  { code: 'me-central-1',   label: 'UAE',               group: 'Middle East & Africa' },
  { code: 'af-south-1',     label: 'Cape Town',         group: 'Middle East & Africa' },
  { code: 'il-central-1',   label: 'Tel Aviv',          group: 'Middle East & Africa' },
];

const REQUIRED_REGIONS = 20;

const REGION_GROUPS = Array.from(
  ALL_REGIONS.reduce((acc, r) => {
    if (!acc.has(r.group)) acc.set(r.group, []);
    acc.get(r.group)!.push(r);
    return acc;
  }, new Map<string, typeof ALL_REGIONS>()).entries()
);

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: string }) {
  const colorMap: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
    pending:        'default',
    validated:      'info',
    provisioning:   'warning',
    active:         'success',
    failed:         'error',
    decommissioned: 'default',
  };
  return (
    <Chip
      size="small"
      label={status.toUpperCase()}
      color={colorMap[status] ?? 'default'}
      variant="outlined"
      sx={{ fontWeight: 700, fontSize: '0.65rem' }}
    />
  );
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : (label ?? 'Copy')}>
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
        sx={{ ml: 0.5 }}
      >
        <CopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Wizard for one monitor unit  (create + manage lifecycle)
// ---------------------------------------------------------------------------

type WizardStep = 'setup' | 'connect' | 'regions' | 'provision' | 'active';

interface UnitWizardProps {
  /** Existing DB record — null when creating a brand-new unit */
  mu: MonitorUnitData | null;
  onUpdated: (updated: MonitorUnitData) => void;
  onDecommissioned: () => void;
  /** Called after successful creation so the parent can add the new record */
  onCreated: (created: MonitorUnitData) => void;
}

const UnitWizard: React.FC<UnitWizardProps> = ({ mu, onUpdated, onDecommissioned, onCreated }) => {
  const theme = useTheme();
  const { showNotification } = useNotification();

  const [cfData, setCfData] = useState<{
    external_id: string; yaml: string; aws_cli_command: string; launch_stack_url: string;
  } | null>(null);
  const [cfError, setCfError] = useState<string | null>(null);
  const [showManualOptions, setShowManualOptions] = useState(false);
  const [setupDone, setSetupDone]     = useState(false);

  // Connect step
  const [nameInput, setNameInput]     = useState(mu?.name ?? '');
  const [accountId, setAccountId]     = useState('');
  const [roleArn, setRoleArn]         = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting]   = useState(false);

  // Validate step
  const [validating, setValidating]   = useState(false);
  const [validateResult, setValidateResult] = useState<{ success: boolean; details: string } | null>(null);

  // Regions step
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(
    new Set(mu?.selected_regions ?? [])
  );
  const [savingRegions, setSavingRegions] = useState(false);

  // Provision step
  const [provisioning, setProvisioning] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Decommission
  const [decommissioning, setDecommissioning]     = useState(false);
  const [confirmDecommission, setConfirmDecommission] = useState(false);

  // Update Lambda code
  const [updatingCode, setUpdatingCode] = useState(false);
  const [codeUpdateAvailable, setCodeUpdateAvailable] = useState(false);

  // Change regions on active unit
  const [editingRegions, setEditingRegions] = useState(false);

  // Load CF template once (for setup step)
  const loadCfTemplate = useCallback(() => {
    setCfError(null);
    setCfData(null);
    api.getMonitorUnitCfTemplate()
      .then(setCfData)
      .catch((err: any) => setCfError(err?.response?.data?.detail ?? 'Failed to load template — check backend logs'));
  }, []);

  useEffect(() => {
    if (!mu) loadCfTemplate();
  }, [mu, loadCfTemplate]);

  // Check for Lambda code updates when unit is active
  const muId = mu?.id;
  const muStatus = mu?.status;
  useEffect(() => {
    if (!muId || muStatus !== 'active') return;
    let cancelled = false;
    api.checkMonitorUnitCodeUpdate(muId)
      .then((r) => { if (!cancelled) setCodeUpdateAvailable(r.update_available); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [muId, muStatus]);

  // Poll during provisioning
  useEffect(() => {
    if (mu?.status === 'provisioning') {
      pollRef.current = setInterval(async () => {
        try {
          const fresh = await api.getMonitorUnitProvisionStatus(mu.id);
          onUpdated(fresh);
          if (fresh.status !== 'provisioning') {
            clearInterval(pollRef.current!);
            showNotification(
              fresh.status === 'active'
                ? 'Monitor unit provisioned successfully!'
                : 'Provisioning completed with errors. Check the provision log.',
              fresh.status === 'active' ? 'success' : 'warning',
            );
          }
        } catch { /* ignore */ }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [mu?.status, mu?.id, onUpdated, showNotification]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const wizardStep = (): WizardStep => {
    if (!mu && !setupDone) return 'setup';
    if (!mu)               return 'connect';
    if (mu.status === 'pending')      return 'connect';
    if (mu.status === 'validated') {
      return (mu.selected_regions?.length ?? 0) === REQUIRED_REGIONS ? 'provision' : 'regions';
    }
    if (mu.status === 'provisioning') return 'provision';
    if (mu.status === 'active')  return editingRegions ? 'regions' : 'active';
    if (mu.status === 'failed')  return editingRegions ? 'regions' : 'provision';
    return 'setup';
  };
  const step = wizardStep();

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleLaunchStack = () => {
    if (cfData?.launch_stack_url) window.open(cfData.launch_stack_url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadCf = () => {
    if (!cfData) return;
    const blob = new Blob([cfData.yaml], { type: 'text/yaml' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'caosblitz-monitor-unit.yaml'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleConnect = async () => {
    if (!cfData) return;
    setConnectError(null);
    if (!/^\d{12}$/.test(accountId.trim())) {
      setConnectError('Account ID must be exactly 12 digits'); return;
    }
    if (!roleArn.trim().startsWith('arn:aws:iam::')) {
      setConnectError('Role ARN must start with arn:aws:iam::'); return;
    }
    setConnecting(true);
    try {
      const created = await api.createMonitorUnit({
        name:             nameInput.trim() || undefined,
        aws_account_id:   accountId.trim(),
        role_arn:         roleArn.trim(),
        external_id:      cfData.external_id,
        selected_regions: [],
      });
      onCreated(created);
      showNotification('Monitor unit registered — now validate the connection', 'success');
    } catch (err: any) {
      setConnectError(err.response?.data?.detail ?? 'Failed to create monitor unit');
    } finally {
      setConnecting(false);
    }
  };

  const handleValidate = async () => {
    if (!mu) return;
    setValidating(true);
    setValidateResult(null);
    try {
      const result = await api.validateMonitorUnit(mu.id);
      setValidateResult(result);
      if (result.success) {
        const fresh = await api.listMonitorUnits();
        const updated = fresh.find(u => u.id === mu.id);
        if (updated) onUpdated(updated);
        showNotification('Connection validated successfully!', 'success');
      }
    } catch (err: any) {
      setValidateResult({ success: false, details: err.response?.data?.detail ?? 'Validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const handleSaveRegions = async () => {
    if (!mu) return;
    setSavingRegions(true);
    try {
      const fresh = await api.updateMonitorUnitRegions(mu.id, Array.from(selectedRegions));
      onUpdated(fresh);

      // For active units, automatically kick off provisioning for new regions
      if (mu.status === 'active') {
        const currentProvisioned = new Set(mu.provisioned_regions ?? []);
        const hasNewRegions = Array.from(selectedRegions).some(r => !currentProvisioned.has(r));
        if (hasNewRegions) {
          const provisioned = await api.provisionMonitorUnit(fresh.id);
          onUpdated(provisioned);
          showNotification('Regions saved — deploying Lambda to new regions…', 'info');
        } else {
          showNotification('Regions saved', 'success');
        }
      } else {
        showNotification('Regions saved', 'success');
      }
      setEditingRegions(false);
    } catch (err: any) {
      showNotification(err.response?.data?.detail ?? 'Failed to save regions', 'error');
    } finally {
      setSavingRegions(false);
    }
  };

  const handleProvision = async () => {
    if (!mu) return;
    setProvisioning(true);
    try {
      const fresh = await api.provisionMonitorUnit(mu.id);
      onUpdated(fresh);
    } catch (err: any) {
      showNotification(err.response?.data?.detail ?? 'Failed to start provisioning', 'error');
    } finally {
      setProvisioning(false);
    }
  };

  const handleDecommission = async () => {
    if (!mu) return;
    setDecommissioning(true);
    setConfirmDecommission(false);
    try {
      await api.decommissionMonitorUnit(mu.id);
      showNotification('Monitor unit decommissioned', 'success');
      onDecommissioned();
    } catch (err: any) {
      showNotification(err.response?.data?.detail ?? 'Decommission failed', 'error');
    } finally {
      setDecommissioning(false);
    }
  };

  const handleUpdateCode = async () => {
    if (!mu) return;
    setUpdatingCode(true);
    try {
      const result = await api.updateMonitorUnitLambdaCode(mu.id);
      const ok  = result.updated?.length ?? 0;
      const bad = result.failed?.length ?? 0;
      if (bad === 0) {
        showNotification(`Lambda code updated in ${ok} region(s)`, 'success');
        setCodeUpdateAvailable(false);
      } else {
        showNotification(`Updated ${ok} region(s), failed ${bad}`, 'warning');
      }
    } catch (err: any) {
      showNotification(err.response?.data?.detail ?? 'Lambda code update failed', 'error');
    } finally {
      setUpdatingCode(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderSetupStep = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Run the CloudFormation stack in your AWS account to create the IAM trust role that
        CaosBlitz will use to deploy probe infrastructure.
      </Typography>

      {cfData ? (
        <Stack spacing={2}>
          {cfData.launch_stack_url ? (
            <Box>
              <Button variant="contained" size="medium" startIcon={<LaunchIcon />} onClick={handleLaunchStack} sx={{ fontWeight: 600 }}>
                Open in AWS Console
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Opens the CloudFormation review page with all parameters pre-filled. Click <strong>Create stack</strong> to proceed.
              </Typography>
            </Box>
          ) : (
            <Alert severity="info" sx={{ py: 0.5 }}>Hosted template URL not configured — use the manual options below.</Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              ExternalId:&nbsp;<Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>{cfData.external_id}</Box>
            </Typography>
            <CopyButton value={cfData.external_id} label="Copy ExternalId" />
          </Box>

          <Box>
            <Link component="button" variant="caption" underline="hover" color="text.secondary"
              onClick={() => setShowManualOptions(v => !v)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer' }}
            >
              <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 0.2s', transform: showManualOptions ? 'rotate(180deg)' : 'none' }} />
              {showManualOptions ? 'Hide manual options' : 'Manual / CLI options'}
            </Link>
            <Collapse in={showManualOptions}>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" onClick={handleDownloadCf}>Download YAML</Button>
                  <CopyButton value={cfData.yaml} label="Copy YAML" />
                </Box>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>AWS CLI command:</Typography>
                  <Box component="pre" sx={{ m: 0, fontSize: '0.72rem', overflowX: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{cfData.aws_cli_command}</Box>
                  <CopyButton value={cfData.aws_cli_command} label="Copy command" />
                </Paper>
              </Stack>
            </Collapse>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" size="small" onClick={() => setSetupDone(true)}>
              Stack deployed — Continue
            </Button>
            <Typography variant="caption" color="text.secondary">Copy the <strong>RoleArn</strong> from the stack Outputs tab first.</Typography>
          </Box>
        </Stack>
      ) : cfError ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Alert severity="error" sx={{ py: 0.5, flex: 1 }}>{cfError}</Alert>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadCfTemplate}>Retry</Button>
        </Box>
      ) : (
        <CircularProgress size={20} />
      )}
    </Box>
  );

  const renderConnectStep = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the CloudFormation stack outputs to connect CaosBlitz to your AWS account.
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          label="Label (optional)"
          value={nameInput}
          onChange={e => setNameInput(e.target.value)}
          size="small" fullWidth
          placeholder="e.g. Production, Staging…"
          helperText="Helps identify this unit in the list"
        />
        <TextField label="AWS Account ID" value={accountId} onChange={e => setAccountId(e.target.value)}
          size="small" fullWidth placeholder="123456789012" helperText="12-digit account ID (no dashes)" />
        <TextField label="Role ARN" value={roleArn} onChange={e => setRoleArn(e.target.value)}
          size="small" fullWidth placeholder="arn:aws:iam::123456789012:role/CaosBlitzMonitorRole" />
        {connectError && <Alert severity="error" sx={{ py: 0 }}>{connectError}</Alert>}
        <Button variant="contained" onClick={handleConnect}
          disabled={connecting || !accountId || !roleArn}
          startIcon={connecting ? <CircularProgress size={16} /> : undefined}
        >
          {connecting ? 'Saving…' : 'Save & Continue'}
        </Button>
      </Stack>
    </Box>
  );

  const renderValidateStep = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verify that CaosBlitz can assume the role in your AWS account.
      </Typography>
      {validateResult && (
        <Alert severity={validateResult.success ? 'success' : 'error'} sx={{ mb: 1.5, py: 0 }}
          icon={validateResult.success ? <CheckCircleIcon /> : <ErrorIcon />}>
          {validateResult.details}
        </Alert>
      )}
      <Button variant="outlined" onClick={handleValidate} disabled={validating}
        startIcon={validating ? <CircularProgress size={16} /> : <RefreshIcon />}>
        {validating ? 'Validating…' : 'Test Connection'}
      </Button>
    </Box>
  );

  const renderRegionsStep = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Select exactly {REQUIRED_REGIONS} AWS regions to deploy probe infrastructure into.
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Chip size="small"
          label={`${selectedRegions.size} / ${REQUIRED_REGIONS} selected`}
          color={selectedRegions.size === REQUIRED_REGIONS ? 'success' : selectedRegions.size > REQUIRED_REGIONS ? 'error' : 'warning'}
          variant="outlined"
        />
      </Box>
      {REGION_GROUPS.map(([group, regs], gi) => (
        <Box key={group}>
          {gi > 0 && <Divider sx={{ my: 1 }} />}
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>{group}</Typography>
          <FormGroup>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 0 }}>
              {regs.map(r => {
                const checked  = selectedRegions.has(r.code);
                const atLimit  = selectedRegions.size >= REQUIRED_REGIONS;
                const disabled = !checked && atLimit;
                return (
                  <FormControlLabel key={r.code} disabled={disabled}
                    control={<Checkbox size="small" checked={checked} sx={{ py: 0.5 }}
                      onChange={() => setSelectedRegions(prev => {
                        const next = new Set(prev);
                        if (next.has(r.code)) next.delete(r.code); else next.add(r.code);
                        return next;
                      })} />}
                    label={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>{r.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.65rem' }}>{r.code}</Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </FormGroup>
        </Box>
      ))}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button variant="contained" onClick={handleSaveRegions}
          disabled={savingRegions || selectedRegions.size !== REQUIRED_REGIONS}
          startIcon={savingRegions ? <CircularProgress size={16} /> : undefined}>
          {savingRegions
            ? 'Saving…'
            : mu?.status === 'active'
              ? 'Save & Deploy New Regions'
              : 'Save Regions'}
        </Button>
        {mu?.status === 'active' && (
          <Typography variant="caption" color="text.secondary">
            New regions will be deployed automatically
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderProvisionStep = () => {
    const isProvisioning = mu?.status === 'provisioning';
    const isFailed       = mu?.status === 'failed';
    const provLog        = mu?.provision_log ?? {};
    const errorEntry     = provLog['__error__'];
    const provisionedCount = mu?.provisioned_regions?.length ?? 0;
    const totalRegions     = mu?.selected_regions?.length ?? 0;

    return (
      <Box>
        {isFailed && (
          <Button variant="contained" onClick={handleProvision} disabled={provisioning}
            startIcon={provisioning ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{ mb: 1.5 }}>
            {provisioning ? 'Starting…' : 'Retry Provisioning'}
          </Button>
        )}
        {!isProvisioning && (mu?.status === 'validated' || !mu?.provisioned_at) && !isFailed && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              CaosBlitz will deploy the probe-runner Lambda to each of your {totalRegions} selected
              regions and create the EventBridge Scheduler group. This takes ~2–3 minutes.
            </Typography>
            <Button variant="contained" onClick={handleProvision} disabled={provisioning}
              startIcon={provisioning ? <CircularProgress size={16} /> : undefined}>
              {provisioning ? 'Starting…' : 'Deploy Monitor Unit'}
            </Button>
          </>
        )}
        {isProvisioning && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Deploying Lambda to regions… ({provisionedCount}/{totalRegions} complete)</Typography>
            <LinearProgress variant={totalRegions > 0 ? 'determinate' : 'indeterminate'}
              value={totalRegions > 0 ? (provisionedCount / totalRegions) * 100 : undefined}
              sx={{ mb: 1.5, borderRadius: 1 }} />
          </Box>
        )}
        {errorEntry && <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>{String(errorEntry)}</Alert>}
        {!errorEntry && isFailed && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>Some regions failed to provision. You can retry.</Alert>
        )}
        {Object.keys(provLog).filter(k => !k.startsWith('__')).length > 0 && (
          <Box sx={{ mt: 1.5, maxHeight: 200, overflowY: 'auto' }}>
            {Object.entries(provLog).filter(([k]) => !k.startsWith('__')).map(([region, res]: [string, any]) => (
              <Box key={region} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                {res.status === 'success'
                  ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  : <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{region}</Typography>
                {res.error && <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>{String(res.error).slice(0, 80)}</Typography>}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderActiveStep = () => (
    <Box>
      <Stack spacing={0.75} sx={{ mb: 2 }}>
        <Typography variant="body2"><strong>Account ID:</strong>&nbsp;{mu?.aws_account_id}</Typography>
        <Typography variant="body2">
          <strong>Role ARN:</strong>&nbsp;
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{mu?.role_arn}</Box>
        </Typography>
        <Typography variant="body2">
          <strong>Provisioned regions:</strong>&nbsp;{mu?.provisioned_regions?.length ?? 0} / {mu?.selected_regions?.length ?? 0}
        </Typography>
        {mu?.provisioned_at && (
          <Typography variant="body2"><strong>Provisioned at:</strong>&nbsp;{new Date(mu.provisioned_at).toLocaleString()}</Typography>
        )}
      </Stack>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {(mu?.provisioned_regions ?? []).map(r => (
          <Chip key={r} label={r} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
        ))}
      </Box>

      {/* ── Change regions ── */}
      <Box sx={{ mb: 2 }}>
        <Link component="button" variant="body2" underline="hover" color="primary"
          onClick={() => setEditingRegions(v => !v)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', fontWeight: 600 }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18, transition: 'transform 0.2s', transform: editingRegions ? 'rotate(180deg)' : 'none' }} />
          {editingRegions ? 'Hide region selector' : 'Change regions…'}
        </Link>
        <Collapse in={editingRegions}>
          <Box sx={{ mt: 1.5 }}>
            {renderRegionsStep()}
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
        <Button
          variant={codeUpdateAvailable ? 'contained' : 'outlined'}
          color={codeUpdateAvailable ? 'primary' : 'inherit'}
          size="small"
          startIcon={updatingCode ? <CircularProgress size={14} /> : <UpdateIcon />}
          onClick={handleUpdateCode}
          disabled={updatingCode}
        >
          {updatingCode ? 'Updating…' : 'Update Lambda Code'}
        </Button>
        {codeUpdateAvailable && (
          <Chip label="New version available" size="small" color="info" variant="outlined" />
        )}
      </Box>

      {!confirmDecommission ? (
        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
          onClick={() => setConfirmDecommission(true)} disabled={decommissioning}>
          Decommission
        </Button>
      ) : (
        <Alert severity="error" sx={{ py: 0.5 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" color="inherit" onClick={() => setConfirmDecommission(false)}>Cancel</Button>
              <Button size="small" color="error" variant="contained" onClick={handleDecommission} disabled={decommissioning}
                startIcon={decommissioning ? <CircularProgress size={14} /> : undefined}>
                {decommissioning ? 'Removing…' : 'Confirm'}
              </Button>
            </Box>
          }
        >
          This will delete all Lambda functions and EventBridge schedules. Are you sure?
        </Alert>
      )}
    </Box>
  );

  // ── Stepper ──────────────────────────────────────────────────────────────
  const STEPS = [
    { label: 'Run CloudFormation', id: 'setup'     as WizardStep },
    { label: 'Connect Account',    id: 'connect'   as WizardStep },
    { label: 'Select Regions',     id: 'regions'   as WizardStep },
    { label: 'Deploy',             id: 'provision' as WizardStep },
  ];
  const activeStepIndex = (s: WizardStep) =>
    s === 'setup' ? 0 : s === 'connect' ? 1 : s === 'regions' ? 2 : 3;

  if (step === 'active') return renderActiveStep();

  return (
    <Stepper activeStep={activeStepIndex(step)} orientation="vertical">
      {STEPS.map(({ label, id }, idx) => (
        <Step key={id} completed={activeStepIndex(step) > idx}>
          <StepLabel>{label}</StepLabel>
          <StepContent>
            {id === 'setup'   && renderSetupStep()}
            {id === 'connect' && (
              <Box>
                {renderConnectStep()}
                {mu && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Connection test</Typography>
                    {renderValidateStep()}
                  </>
                )}
              </Box>
            )}
            {id === 'regions'   && renderRegionsStep()}
            {id === 'provision' && renderProvisionStep()}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};

// ---------------------------------------------------------------------------
// Main panel — list on left, wizard on right
// ---------------------------------------------------------------------------

interface Props {
  onDecommissioned?: () => void;
}

const MonitorUnitCard: React.FC<Props> = ({ onDecommissioned }) => {
  const { showNotification } = useNotification();

  const [loading, setLoading]   = useState(true);
  const [units, setUnits]       = useState<MonitorUnitData[]>([]);
  // ID of the unit currently shown in the wizard, or 'new' for the add form
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [activating, setActivating] = useState<string | null>(null);

  const loadUnits = useCallback(async () => {
    try {
      const list = await api.listMonitorUnits();
      setUnits(list);
      // Auto-select: active unit first, else first in list, else 'new'
      if (list.length > 0) {
        const active = list.find(u => u.is_active) ?? list[0];
        setSelectedId(prev => prev ?? active.id);
      } else {
        setSelectedId(prev => prev ?? 'new');
      }
    } catch {
      setUnits([]);
      setSelectedId('new');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  const handleUpdated = useCallback((updated: MonitorUnitData) => {
    setUnits(prev => prev.map(u => u.id === updated.id ? updated : u));
  }, []);

  const handleCreated = useCallback((created: MonitorUnitData) => {
    setUnits(prev => [...prev, created]);
    setSelectedId(created.id);
  }, []);

  const handleDecommissioned = useCallback(() => {
    const removedId = selectedId;
    setUnits(prev => {
      const next = prev.filter(u => u.id !== removedId);
      setSelectedId(next.length > 0 ? (next.find(u => u.is_active) ?? next[0]).id : 'new');
      return next;
    });
    onDecommissioned?.();
  }, [selectedId, onDecommissioned]);

  const handleActivate = async (mu: MonitorUnitData) => {
    setActivating(mu.id);
    try {
      const updated = await api.activateMonitorUnit(mu.id);
      setUnits(prev => prev.map(u => ({ ...u, is_active: u.id === updated.id })));
      showNotification(`"${mu.name ?? mu.aws_account_id}" set as active monitor unit`, 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.detail ?? 'Failed to activate', 'error');
    } finally {
      setActivating(null);
    }
  };

  const selectedMu = units.find(u => u.id === selectedId) ?? null;

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Monitor Units"
        subheader="Cross-account synthetic monitoring via EventBridge Scheduler"
        avatar={<MonitorIcon color="primary" />}
        action={
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadUnits} sx={{ mr: 1 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        }
      />

      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

          {/* ── Left: unit list ─────────────────────────────────────── */}
          <Box sx={{ width: 240, flexShrink: 0 }}>
            <List dense disablePadding sx={{ mb: 1 }}>
              {units.map(mu => (
                <ListItemButton
                  key={mu.id}
                  selected={selectedId === mu.id}
                  onClick={() => setSelectedId(mu.id)}
                  sx={{ borderRadius: 1, mb: 0.5, pr: 6 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Tooltip title={mu.is_active ? 'Active — used for executions' : 'Not active'}>
                          {mu.is_active
                            ? <ActiveIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            : <InactiveIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                        </Tooltip>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                          {mu.name ?? mu.aws_account_id ?? 'Unnamed'}
                        </Typography>
                      </Box>
                    }
                    secondary={<StatusChip status={mu.status} />}
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                  <ListItemSecondaryAction>
                    {!mu.is_active && (
                      <Tooltip title="Set as active">
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={activating === mu.id}
                          onClick={e => { e.stopPropagation(); handleActivate(mu); }}
                        >
                          {activating === mu.id
                            ? <CircularProgress size={14} />
                            : <ActiveIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))}

              {/* Add new */}
              <ListItemButton
                selected={selectedId === 'new'}
                onClick={() => setSelectedId('new')}
                sx={{ borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}
              >
                <AddIcon sx={{ fontSize: 16, mr: 0.75, color: 'text.secondary' }} />
                <ListItemText primary={<Typography variant="body2" color="text.secondary">Add monitor unit</Typography>} />
              </ListItemButton>
            </List>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* ── Right: wizard / active view ─────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0, maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
            {selectedId === 'new' ? (
              <UnitWizard
                key="new"
                mu={null}
                onUpdated={() => {}}
                onDecommissioned={() => {}}
                onCreated={handleCreated}
              />
            ) : selectedMu ? (
              <UnitWizard
                key={selectedMu.id}
                mu={selectedMu}
                onUpdated={handleUpdated}
                onDecommissioned={handleDecommissioned}
                onCreated={handleCreated}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Select a monitor unit from the list.</Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonitorUnitCard;
